import { createSlice } from "@reduxjs/toolkit"
import { persistReducer } from "redux-persist"
import storage from "redux-persist/lib/storage"

import { COMPARE_IDS } from "../../../Components/Filters/CompareSelect/presets"
import { logout } from "../../LoginPage/redux/auth.actions"
import { ANALYTICS_SLICE_NAME as SLICE_NAME } from "./analytics.constants"
import { SECTION_THUNKS } from "./analytics.thunks"

/* ------------- Initial State ------------- */

// The calendar, the comparison and the case-type scope. They live here rather than in the
// page so the five inner pages read the same window the merchant picked on the main one —
// the demo carried it across in localStorage, which this replaces.
export const DEFAULT_ANALYTICS_FILTERS = {
  // "Last 4 weeks", the same preset the Prevent overview opens on.
  period: "27 days",
  // Opens with no comparison: the page's own numbers first, the previous-period overlay
  // only once the merchant asks for it.
  compare: { mode: COMPARE_IDS.NONE },
  lastCompare: null,
  // Case type (dispute | inquiry). Empty = every type; it narrows every block on the page.
  dispute_type: []
}

// Section responses by store key (ANALYTICS_SECTION). `requestIds` holds the latest request per
// key: a response that is not the latest one arrives after the merchant has moved on (another
// period, another page) and is dropped instead of overwriting the newer answer.
const initialState = {
  filters: { ...DEFAULT_ANALYTICS_FILTERS },
  data: {},
  fetching: {},
  requestIds: {}
}

/* ------------- Slice ------------- */

const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    changeAnalyticsFilters(state, { payload }) {
      state.filters = { ...state.filters, ...(payload ?? {}) }
    }
  },
  extraReducers: (builder) => {
    builder.addCase(logout, () => initialState)

    // Several keys share one endpoint (a card and its full page), so each thunk is registered
    // once and writes to whichever key the caller passed.
    new Set(Object.values(SECTION_THUNKS)).forEach((thunk) => {
      builder
        .addCase(thunk.pending, (state, { meta }) => {
          state.fetching[meta.arg.key] = true
          state.requestIds[meta.arg.key] = meta.requestId
        })
        .addCase(thunk.fulfilled, (state, { payload, meta }) => {
          const { key } = meta.arg
          if (state.requestIds[key] !== meta.requestId) return
          state.fetching[key] = false
          state.data[key] = payload
        })
        .addCase(thunk.rejected, (state, { meta }) => {
          const { key } = meta.arg
          if (state.requestIds[key] !== meta.requestId) return
          state.fetching[key] = false
          state.data[key] = null
        })
    })
  }
})

export const { changeAnalyticsFilters } = slice.actions

/* ------------- Selectors ------------- */

export const selectAnalyticsFilters = (state) => state.disputesAnalytics.filters
export const selectSectionData = (key) => (state) => state.disputesAnalytics.data[key]
export const selectSectionFetching = (key) => (state) =>
  Boolean(state.disputesAnalytics.fetching[key])

/* ------------- Persistence ------------- */
// Only the merchant's own window survives a visit: section data is refetched on every mount,
// and persisting it would paint stale figures for a frame before the fresh answer lands.

const PERSISTED_REDUCER_VERSION = 2

export const persistedReducer = persistReducer(
  {
    key: SLICE_NAME,
    storage,
    version: PERSISTED_REDUCER_VERSION,
    blacklist: ["data", "fetching", "requestIds"],
    migrate: async (state) => {
      if (state?._persist?.version < PERSISTED_REDUCER_VERSION) return undefined
      return state
    }
  },
  slice.reducer
)

export default slice.reducer
